import { useEffect, useRef, useState } from 'react';

/**
 * Tracks a ref'd element's rendered content width (px) via ResizeObserver.
 * Used to drive responsive layout decisions (e.g. collapsing a two-column
 * table to one column) based on actual available space rather than a
 * viewport breakpoint — needed because floating Right Panel tabs can each be
 * resized independently to different widths.
 */
export const useElementWidth = <T extends HTMLElement>(): {
  ref: React.RefObject<T>;
  width: number;
} => {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    setWidth(el.getBoundingClientRect().width);

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, width };
};
